import React, { useContext, useEffect, useState, useCallback } from "react";
import {
  User,
  onAuthStateChanged,
  getIdToken,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { auth } from "../config/firebase";

// Immediate module-level debug check
console.log("AUTH_CONTEXT: Module loaded, auth object state:", {
  authExists: !!auth,
  authAppExists: auth?.app ? true : false,
  timestamp: new Date().toISOString(),
});

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  forceAuthCheck: () => Promise<User | null>;
}

export const AuthContext = React.createContext<AuthContextType>({
  currentUser: null,
  loading: true,
  signInWithGoogle: async () => {},
  logout: async () => {},
  forceAuthCheck: async () => null,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authInitCalled, setAuthInitCalled] = useState(false);
  const [autoSignInAttempted, setAutoSignInAttempted] = useState(false);

  // Add SignInWithGoogle function - MOVED UP BEFORE useEffect hooks
  const signInWithGoogle = useCallback(async (): Promise<void> => {
    try {
      console.log("Starting sign in with Google process");
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      console.log("Google sign in successful:", {
        user: result.user ? "exists" : "null",
        uid: result.user?.uid,
        email: result.user?.email,
      });
    } catch (error) {
      console.error("Google sign in failed:", error);
      throw error;
    }
  }, []);

  // Add logout function - MOVED UP BEFORE useEffect hooks
  const logout = async (): Promise<void> => {
    try {
      console.log("Starting sign out process");
      await auth.signOut();

      // Clear Firebase auth data from localStorage to prevent auto-reconnection
      localStorage.removeItem(
        "firebase:authUser:" + auth.app.options.apiKey + ":[DEFAULT]"
      );

      console.log("Sign out successful");

      // Add signedOut parameter to URL
      const redirectUrl =
        window.location.hostname === "localhost"
          ? "/?signedOut=true"
          : "https://app.usmedex.com?signedOut=true";

      window.location.href = redirectUrl;
    } catch (error) {
      console.error("Sign out failed:", error);
      throw error;
    }
  };

  // Check if the user recently signed out
  const hasRecentlySignedOut = () => {
    // Check URL params for signedOut=true
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("signedOut") === "true";
  };

  // Check if we're likely coming from a cross-domain navigation or refreshing with auth
  const isCrossDomainNavigation = useCallback(() => {
    // If the user just signed out, don't treat this as a cross-domain navigation or refresh
    if (hasRecentlySignedOut()) {
      console.log("User recently signed out, skipping auto sign-in");
      return false;
    }

    const hostname = window.location.hostname;
    const referrer = document.referrer;

    // Check for Firebase auth data in localStorage
    const hasStoredAuth = localStorage.getItem(
      `firebase:authUser:${auth.app.options.apiKey}:[DEFAULT]`
    );
    console.log("Checking for stored auth:", {
      hasStoredAuth: !!hasStoredAuth,
      hostname,
      referrer: referrer || "(empty)",
    });

    // If we're on localhost or booking.usmedex.com
    const isBookingDomain =
      hostname === "localhost" ||
      hostname === "localhost:3000" ||
      hostname.includes("booking.usmedex.com");

    // And referrer contains the main domain or is empty (refresh case)
    const isFromMainDomain = referrer.includes("app.usmedex.com");

    // Consider it a refresh if the referrer is empty or from the same domain
    const isRefresh = !referrer || referrer.includes(hostname);

    // Handle three cases:
    // 1. Cross-domain navigation from main app to booking
    // 2. Page refresh with stored auth data
    // 3. Direct navigation to booking with stored auth data
    return (
      (isBookingDomain && isFromMainDomain) || // Cross-domain navigation
      (isBookingDomain && hasStoredAuth && isRefresh) // Page refresh with auth data
    );
  }, []);

  // Function to check if we have cached credentials from localStorage/indexedDB
  const checkPersistentAuth = async () => {
    // Delay to ensure Firebase has time to restore auth state from storage
    await new Promise((resolve) => setTimeout(resolve, 1000));

    if (auth.currentUser) {
      const user = auth.currentUser;
      console.log("Auth restored from persistent storage:", {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        providerId: user.providerId,
      });

      try {
        // Force token refresh to ensure validity
        await getIdToken(user, true);
        setCurrentUser(user);
        setLoading(false);
        return true;
      } catch (error) {
        console.error("Error refreshing persistent token:", error);
      }
    }
    return false;
  };

  // Set up the auth state listener
  const setupAuthListener = () => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log("Auth state changed:", {
        user: user ? "exists" : "null",
        isAuthenticated: !!user,
        userId: user?.uid,
        email: user?.email,
        emailVerified: user?.emailVerified,
        displayName: user?.displayName,
        authProvider: user?.providerId,
        currentTime: new Date().toISOString(),
        hostname: window.location.hostname,
      });

      // If user exists, refresh the token to ensure it's valid
      if (user) {
        getIdToken(user, true)
          .then((token) => {
            console.log("Token refreshed in auth listener:", {
              tokenPrefix: token.substring(0, 10) + "...",
              length: token.length,
              uid: user.uid,
            });
          })
          .catch((error) => {
            console.error("Error refreshing token in auth listener:", error);
          });
      }

      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  };

  // Automatic sign-in attempt for cross-domain navigation or refresh
  const attemptAutoSignIn = useCallback(async () => {
    if (autoSignInAttempted) {
      console.log("Auto sign-in already attempted this session, skipping");
      return null;
    }

    setAutoSignInAttempted(true);

    // If user recently signed out, skip auto sign-in
    if (hasRecentlySignedOut()) {
      console.log("User recently signed out, skipping automatic sign-in");
      return null;
    }

    console.log("Attempting automatic sign-in", {
      isRefresh:
        !document.referrer ||
        document.referrer.includes(window.location.hostname),
      hasCurrentUser: !!auth.currentUser,
      referrer: document.referrer || "(empty)",
      hostname: window.location.hostname,
    });

    // On page refresh, first check if we already have a user
    if (auth.currentUser) {
      console.log("Found existing auth.currentUser on refresh:", {
        uid: auth.currentUser.uid,
        email: auth.currentUser.email,
      });

      try {
        // Refresh the token to ensure it's valid
        const token = await getIdToken(auth.currentUser, true);
        console.log("Successfully refreshed token for existing user:", {
          tokenPrefix: token.substring(0, 10) + "...",
          uid: auth.currentUser.uid,
        });

        setCurrentUser(auth.currentUser);
        setLoading(false);
        return auth.currentUser;
      } catch (error) {
        console.error("Error refreshing token for existing user:", error);
        // Continue to popup method as fallback
      }
    }

    // If no current user or token refresh failed, try the popup approach
    try {
      console.log(
        "No current user or token refresh failed, attempting silent popup"
      );
      const provider = new GoogleAuthProvider();
      // Try silent mode first by setting a custom parameter
      provider.setCustomParameters({ prompt: "none" });

      const result = await signInWithPopup(auth, provider);

      if (result.user) {
        console.log("Automatic sign-in successful:", {
          uid: result.user.uid,
          email: result.user.email,
        });
        setCurrentUser(result.user);
        setLoading(false);
        return result.user;
      }
    } catch (error) {
      console.log("Automatic sign-in with popup failed:", error);
      // This is expected - we'll fall back to normal auth flow
    }

    return null;
  }, [autoSignInAttempted]);

  // Function to explicitly check auth state and refresh token
  const forceAuthCheck = async () => {
    try {
      console.log("FORCE CHECK: Starting auth check sequence", {
        timestamp: new Date().toISOString(),
        autoSignInAttempted,
        currentUserExists: !!currentUser,
      });

      // First try: immediate check
      const user = auth.currentUser;

      console.log("FORCE CHECK: Initial auth check result:", {
        userExists: !!user,
        uid: user?.uid,
        email: user?.email,
        timeStamp: new Date().toISOString(),
      });

      if (user) {
        // If user exists, refresh token and update state
        try {
          const token = await getIdToken(user, true);
          console.log("FORCE CHECK: Token refresh successful:", {
            tokenPrefix: token.substring(0, 10) + "...",
            length: token.length,
            uid: user.uid,
          });

          // Update state if user changed
          if (!currentUser || currentUser.uid !== user.uid) {
            setCurrentUser(user);
            console.log("FORCE CHECK: Updated currentUser state");
          }

          return user;
        } catch (tokenError) {
          console.error(
            "FORCE CHECK: Token refresh failed, will retry:",
            tokenError
          );
        }
      }

      // Second try: wait 1 second before checking again
      console.log(
        "FORCE CHECK: No valid user found on first try, retrying after 1s delay..."
      );
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const secondTryUser = auth.currentUser;
      console.log("FORCE CHECK: Second try check:", {
        userExists: !!secondTryUser,
        uid: secondTryUser?.uid,
        email: secondTryUser?.email,
      });

      if (secondTryUser) {
        try {
          await getIdToken(secondTryUser, true);
          console.log("FORCE CHECK: Second try successful");
          setCurrentUser(secondTryUser);
          return secondTryUser;
        } catch (error) {
          console.error("FORCE CHECK: Second try token refresh failed:", error);
        }
      }

      // Third try: wait 2 more seconds before checking again
      console.log(
        "FORCE CHECK: No valid user found on second try, retrying after 2s delay..."
      );
      await new Promise((resolve) => setTimeout(resolve, 2000));

      const thirdTryUser = auth.currentUser;
      console.log("FORCE CHECK: Third try check:", {
        userExists: !!thirdTryUser,
        uid: thirdTryUser?.uid,
        email: thirdTryUser?.email,
      });

      if (thirdTryUser) {
        try {
          await getIdToken(thirdTryUser, true);
          console.log("FORCE CHECK: Third try successful");
          setCurrentUser(thirdTryUser);
          return thirdTryUser;
        } catch (error) {
          console.error("FORCE CHECK: Third try token refresh failed:", error);
        }
      }

      // If we still don't have a user, try automatic sign-in as a last resort
      console.log(
        "FORCE CHECK: No user found after multiple attempts, trying auto sign-in..."
      );

      try {
        const autoSignInUser = await attemptAutoSignIn();
        if (autoSignInUser) {
          console.log("FORCE CHECK: Auto sign-in successful");
          return autoSignInUser;
        }
        console.log("FORCE CHECK: Auto sign-in failed or was skipped");
      } catch (autoSignInError) {
        console.error(
          "FORCE CHECK: Error during auto sign-in:",
          autoSignInError
        );
      }

      console.log(
        "FORCE CHECK: All authentication methods exhausted, no user found"
      );
      return null;
    } catch (error) {
      console.error(
        "FORCE CHECK: Unexpected error in force auth check:",
        error
      );
      return null;
    }
  };

  useEffect(() => {
    if (authInitCalled) return;
    setAuthInitCalled(true);

    console.log("AuthContext initialization started", {
      time: new Date().toISOString(),
      hostname: window.location.hostname,
      referrer: document.referrer || "(none)",
      authObject: auth ? "initialized" : "null",
      urlParams: window.location.search,
      hasExistingUser: !!auth.currentUser,
    });

    // Track unsubscribe function to clean up listener
    let unsubscribeAuth: (() => void) | undefined;

    // Check if this is a page refresh with existing auth
    const isRefresh =
      !document.referrer ||
      document.referrer.includes(window.location.hostname);

    // First check if we already have a user on page refresh
    if (isRefresh && auth.currentUser) {
      console.log("Page refresh with existing user detected:", {
        uid: auth.currentUser.uid,
        email: auth.currentUser.email,
      });

      // Update state and refresh token
      setCurrentUser(auth.currentUser);
      getIdToken(auth.currentUser, true)
        .then((token) => {
          console.log("Token refreshed during page refresh:", {
            tokenPrefix: token.substring(0, 10) + "...",
          });
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error refreshing token during page refresh:", error);
          // Fall back to regular auth flow
          unsubscribeAuth = setupAuthListener();
        });
    } else {
      // Check if this is likely a cross-domain navigation that needs automatic auth
      const shouldAttemptAutoSignIn = isCrossDomainNavigation();

      // For cross-domain navigation, try auto sign-in
      if (shouldAttemptAutoSignIn) {
        console.log(
          "Detected potential cross-domain navigation, will try auto sign-in"
        );

        // Start the auth check process right away for cross-domain navigation
        (async () => {
          const user = await attemptAutoSignIn();
          if (user) {
            console.log("Auto sign-in successful, skipping other auth checks");
            return; // Skip the rest of the auth flow if we succeeded
          }

          // If auto sign-in failed, continue with normal auth flow
          const authRestored = await checkPersistentAuth();
          if (!authRestored) {
            console.log("No persistent auth found, setting up listener");
            unsubscribeAuth = setupAuthListener();
          }
        })();
      } else {
        // Standard auth flow for non-cross-domain navigation
        console.log("Standard auth flow - checking persistent auth");

        // FIRST: check if we have cached credentials from localStorage/indexedDB
        (async () => {
          const authRestored = await checkPersistentAuth();

          if (!authRestored) {
            console.log("No persistent auth found, setting up listener");
            unsubscribeAuth = setupAuthListener();

            // Try auto sign-in immediately after setting up listener (no timer)
            // Only if not recently signed out
            if (!hasRecentlySignedOut() && !currentUser) {
              try {
                console.log("AUTH_CONTEXT: Attempting immediate auto sign-in");
                await signInWithGoogle();
                console.log("AUTH_CONTEXT: Auto sign-in successful");
              } catch (error: any) {
                // Silently handle errors during auto-signin
                if (
                  error.code === "auth/popup-closed-by-user" ||
                  error.code === "auth/popup-blocked" ||
                  error.message?.includes("The popup has been closed")
                ) {
                  console.log(
                    "AUTH_CONTEXT: Auto sign-in popup was closed/blocked (expected)"
                  );
                } else {
                  console.error("AUTH_CONTEXT: Auto sign-in failed:", error);
                }
              }
            } else {
              console.log(
                "AUTH_CONTEXT: User already signed in or recently signed out, skipping auto sign-in"
              );
            }
          }
        })();
      }
    }

    // Log persistence settings
    console.log("Auth persistence:", {
      persistenceType: "browserSessionPersistence",
      domain: document.domain,
      cookieEnabled: navigator.cookieEnabled,
    });

    // Clean up the auth listener when component unmounts
    return () => {
      if (unsubscribeAuth) {
        console.log("Cleaning up auth listener");
        unsubscribeAuth();
      }
    };
  }, [
    authInitCalled,
    isCrossDomainNavigation,
    attemptAutoSignIn,
    signInWithGoogle,
    currentUser,
  ]);

  // Log whenever the context value changes
  useEffect(() => {
    console.log("AuthContext value updated:", {
      currentUser: currentUser
        ? {
            uid: currentUser.uid,
            email: currentUser.email,
            isAnonymous: currentUser.isAnonymous,
          }
        : null,
      loading,
      authInitialized: !!auth,
    });
  }, [currentUser, loading]);

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        loading,
        signInWithGoogle,
        logout,
        forceAuthCheck,
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
